import type { FormEvent } from 'react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type {
  CustomerManagerMoneyResponse,
  CustomerManagerMoneyTopCustomer,
  CustomerManagerMoneyTrendDays,
  CustomerManagerPortfolioCustomer,
  CustomerManagerRiskQueueItem,
  CustomerManagerRiskSignal,
  CustomerManagerRunwayResponse,
  CustomerManagerRunwayThresholdDays,
} from '@nadoutong/contracts';
import {
  CUSTOMER_MANAGER_RUNWAY_DEFAULT_THRESHOLD_DAYS,
  CUSTOMER_MANAGER_RUNWAY_THRESHOLD_OPTIONS,
} from '@nadoutong/contracts';
import type { LucideIcon } from 'lucide-react';
import {
  ArrowLeftRight,
  ExternalLink,
  FileSpreadsheet,
  HandCoins,
  LayoutDashboard,
  RefreshCw,
  Trophy,
  Users,
  Wallet,
  Waypoints,
} from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  pendingTransfersByOrgId,
  useMyPendingCustomerTransfers,
} from '../api/approvals.ts';
import { OpsApiError } from '../api/client.ts';
import type { CustomerPortfolioSortKey } from '../api/customerManagerWorkspace.ts';
import {
  useCustomerManagerMoney,
  useCustomerManagerRunway,
  useCustomerManagerWorkbench,
} from '../api/customerManagerWorkspace.ts';
import { usePricing } from '../api/pricing.ts';
import {
  ROLE_OPS_ADMIN,
  ROLE_OPS_CUSTOMER_MANAGER,
  hasAnyOpsRole,
} from '../auth/opsRoles.ts';
import { BrandScopeBanner } from '../components/BrandScopeBanner.tsx';
import { CustomerSummaryCard } from '../components/CustomerSummaryCard.tsx';
import type { CustomerSummaryFact } from '../components/CustomerSummaryCard.tsx';
import { CustomerCell, CustomerCompanyLine } from '../components/identity/CustomerCell.tsx';
import { CustomerTransferRequestDialog } from '../components/CustomerTransferRequestDialog.tsx';
import { PortfolioSortControl } from '../components/PortfolioSortControl.tsx';
import type { PortfolioSortColumn } from '../components/PortfolioSortControl.tsx';
import { ReceiptSubmitDialog } from '../components/ReceiptSubmitDialog.tsx';
import {
  formatLocalTimestamp,
  formatUsdMicrosOrCentsAsCnyDisplay,
  formatUsdMicrosOrCentsAsCnyDisplayBalance,
} from '../components/formatters.ts';
import { StatusChip } from '../components/StatusChip.tsx';
import { useSession } from '../hooks/useSession.tsx';
import { QuotationTab } from './crm/quotation/QuotationTab.tsx';
import { useIsMobile } from '../hooks/useViewport.ts';
import { useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';
import { ownerIdentityLabel } from '../lib/ownerLabel.ts';
import {
  Button,
  Chip,
  EmptyState,
  Input,
  KpiCard,
  PageHeader,
  Panel,
  SortableTh,
  TBody,
  THead,
  Table,
  Tabs,
  TabsList,
  TabsTrigger,
  Td,
  Th,
  Tr,
  buttonClass,
  cx,
} from '../ui/index.ts';
import type { PortfolioSort } from './customerPortfolioSort.ts';
import { nextPortfolioSort } from './customerPortfolioSort.ts';

const PAGE_LIMIT = 50;

/**
 * The workbench opens on its historical order (most recently assigned first,
 * newest customer as the tie-break) and only takes a sort once an operator
 * picks a column — `sort: null` is what ops-api reads as "leave my book
 * alone" (#1938). Unlike the pool, this is a book a manager already knows the
 * shape of; re-ordering it by default would move the rows out from under them.
 */
const DEFAULT_SORT: PortfolioSort | null = null;

type Tab = 'overview' | 'customers' | 'top_spend' | 'top_up_reminders' | 'quotation';

/**
 * Content sections: the overview is the one-glance summary (book KPIs,
 * funnel + trend, risk queue); the customer list owns the search box.
 */
const TABS: readonly { id: Tab; icon: LucideIcon; labelKey: I18nKey }[] = [
  { id: 'overview', icon: LayoutDashboard, labelKey: 'customer_manager_workspace.tab.overview' },
  { id: 'customers', icon: Users, labelKey: 'customer_manager_workspace.tab.customers' },
  { id: 'top_spend', icon: Trophy, labelKey: 'customer_manager_workspace.tab.top_spend' },
  {
    id: 'top_up_reminders',
    icon: Wallet,
    labelKey: 'customer_manager_workspace.tab.top_up_reminders',
  },
  {
    id: 'quotation',
    icon: FileSpreadsheet,
    labelKey: 'customer_manager_workspace.tab.quotation',
  },
];

interface Scope {
  readonly email: string;
  readonly offset: number;
  /** Admin-only cross-brand filter ('' = all brands, #1040). */
  readonly brand: string;
  readonly sort: PortfolioSort | null;
}

function fmtInt(value: number): string {
  return value.toLocaleString('en-US');
}

function truncate(value: string, head = 8, tail = 4): string {
  if (value.length <= head + tail + 3) return value;
  return `${value.slice(0, head)}…${value.slice(-tail)}`;
}

function riskVariant(signal: CustomerManagerRiskSignal): 'error' | 'warning' | 'neutral' {
  if (signal.severity === 'critical') return 'error';
  if (signal.severity === 'warning') return 'warning';
  return 'neutral';
}

function ownerLabel(
  customer: CustomerManagerPortfolioCustomer,
  translate: (key: I18nKey) => string,
): string {
  return ownerIdentityLabel(customer.owner, customer.owner.login_methods, translate);
}

export function CustomerManagerWorkspacePage(): JSX.Element {
  const t = useT();
  const isMobile = useIsMobile();
  const { user } = useSession();
  const isAdmin = hasAnyOpsRole(user?.roles ?? [], [ROLE_OPS_ADMIN]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [draftEmail, setDraftEmail] = useState('');
  const [scope, setScope] = useState<Scope>({
    email: '',
    offset: 0,
    brand: '',
    sort: DEFAULT_SORT,
  });
  const query = useCustomerManagerWorkbench({
    limit: PAGE_LIMIT,
    offset: scope.offset,
    email: scope.email,
    brand: scope.brand.length > 0 ? scope.brand : undefined,
    sort: scope.sort?.key,
    dir: scope.sort?.dir,
  });
  // Workbench money views (epic #1035, #1065): owned-book
  // consumption trend + top customers + activity funnel. All figures
  // are the tenant-charge consumption lens rendered in CNY via the
  // exchange-rate policy pivot — never cash revenue.
  const [moneyDays, setMoneyDays] = useState<CustomerManagerMoneyTrendDays>(30);
  const money = useCustomerManagerMoney({
    days: moneyDays,
    brand: scope.brand.length > 0 ? scope.brand : undefined,
  });
  // 催充值 runway list (epic #1035 E6-D2, #1066): burn-based days-left
  // per owned customer — the primary collections surface (the
  // low-balance risk signal stays in the risk queue as a stock signal).
  // The endpoint returns the whole owned book; the threshold filters
  // client-side.
  const [runwayThreshold, setRunwayThreshold] = useState<CustomerManagerRunwayThresholdDays>(
    CUSTOMER_MANAGER_RUNWAY_DEFAULT_THRESHOLD_DAYS,
  );
  const runway = useCustomerManagerRunway();
  const pricing = usePricing();
  const cnyPerUsd = pricing.data?.policy?.cny_per_usd ?? null;

  const rows = useMemo(() => query.data?.customers ?? [], [query.data]);
  const portfolio = query.data?.portfolio;
  const total = query.data?.total ?? 0;
  const currentOffset = query.data?.offset ?? scope.offset;
  const hasPrev = currentOffset > 0;
  const hasNext = currentOffset + rows.length < total;

  // Customer transfer/release pilot (epic #1030 E1-S5, #1041): row-level
  // entry point for the owning manager + pending chip while a transfer
  // request is open.
  const [transferTarget, setTransferTarget] =
    useState<CustomerManagerPortfolioCustomer | null>(null);
  // Manual receipt entry (epic #1031 E2-R5, #1063): row-level entry point
  // with the customer pinned — ops-api re-proves ownership server-side.
  const [receiptTarget, setReceiptTarget] =
    useState<CustomerManagerPortfolioCustomer | null>(null);
  const pendingTransfers = useMyPendingCustomerTransfers(
    hasAnyOpsRole(user?.roles ?? [], [ROLE_OPS_ADMIN, ROLE_OPS_CUSTOMER_MANAGER]),
  );
  const pendingByOrgId = useMemo(
    () => pendingTransfersByOrgId(pendingTransfers.data),
    [pendingTransfers.data],
  );

  // An unknown or hand-typed ?tab= falls back to the overview.
  useEffect(() => {
    const requested = searchParams.get('tab');
    setActiveTab(TABS.find((tab) => tab.id === requested)?.id ?? 'overview');
  }, [searchParams]);

  function selectTab(value: string): void {
    const match = TABS.find((tab) => tab.id === value);
    if (match === undefined) return;
    setActiveTab(match.id);
    const next = new URLSearchParams(searchParams);
    if (match.id === 'overview') {
      next.delete('tab');
    } else {
      next.set('tab', match.id);
    }
    setSearchParams(next, { replace: true });
  }

  function handleSearch(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setScope((prev) => ({ ...prev, email: draftEmail.trim(), offset: 0 }));
  }

  function handleClear(): void {
    setDraftEmail('');
    setScope((prev) => ({ ...prev, email: '', offset: 0 }));
  }

  // Re-sorting reshuffles the whole book server-side, so the operator's
  // current offset points at an unrelated slice — go back to page one.
  function handleSort(key: CustomerPortfolioSortKey): void {
    setScope((prev) => ({ ...prev, sort: nextPortfolioSort(prev.sort, key), offset: 0 }));
  }

  function handleSortChange(sort: PortfolioSort | null): void {
    setScope((prev) => ({ ...prev, sort, offset: 0 }));
  }

  const ownerSortColumn: PortfolioSortColumn = {
    key: 'owner',
    label: t('customer_manager_workspace.column.owner'),
  };
  const sortColumns: readonly PortfolioSortColumn[] = [
    { key: 'email', label: t('customer_manager_workspace.column.customer') },
    { key: 'org', label: t('customer_manager_workspace.column.org') },
    { key: 'balance', label: t('customer_manager_workspace.column.balance') },
    { key: 'spend_30d', label: t('customer_manager_workspace.column.spend_30d') },
    { key: 'requests', label: t('customer_manager_workspace.column.usage') },
    { key: 'keys', label: t('customer_manager_workspace.column.keys') },
    ...(isAdmin ? [ownerSortColumn] : []),
    { key: 'risks', label: t('customer_manager_workspace.column.risks') },
  ];

  // The overview and the customer list read the same workbench query, and the
  // overview and top-spend tabs the same money query: each failure banner
  // shows on every tab its query feeds.
  const workbenchError = query.isError ? (
    <ErrorBanner
      testId="customer-manager-error"
      title={t('customer_manager_workspace.error.title')}
      error={query.error}
      onRetry={() => void query.refetch()}
    />
  ) : null;
  const moneyError = money.isError ? (
    <ErrorBanner
      testId="customer-manager-money-error"
      title={t('customer_manager_workspace.money.error.title')}
      error={money.error}
      onRetry={() => void money.refetch()}
    />
  ) : null;

  return (
    <section data-testid="customer-manager-workspace-page" className="flex flex-col gap-6">
      <PageHeader
        title={t('customer_manager_workspace.title')}
        sub={
          isAdmin
            ? t('customer_manager_workspace.subtitle.admin')
            : t('customer_manager_workspace.subtitle.manager')
        }
        actions={
          <>
            <span className="font-mono text-micro text-paper-low">
              {query.data?.generated_at !== undefined
                ? t('customer_manager_workspace.generated_at', {
                    time: formatLocalTimestamp(query.data.generated_at),
                  })
                : t('customer_manager_workspace.generated_pending')}
            </span>
            <Button
              onClick={() => void query.refetch()}
              data-testid="customer-manager-refresh"
            >
              <RefreshCw aria-hidden="true" className="h-3.5 w-3.5" />
              {t('common.refresh')}
            </Button>
          </>
        }
      />

      <BrandScopeBanner
        testIdPrefix="customer-manager-workspace"
        brandFilter={scope.brand}
        onBrandFilterChange={(brand) =>
          setScope((prev) => ({ ...prev, brand, offset: 0 }))
        }
      />

      <Tabs value={activeTab} onValueChange={selectTab}>
        <TabsList
          ariaLabel={t('customer_manager_workspace.tabs_aria')}
          className="max-w-full overflow-x-auto"
        >
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <TabsTrigger
                key={tab.id}
                value={tab.id}
                testId={`customer-manager-workspace-tab-${tab.id}`}
              >
                <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                  <Icon aria-hidden="true" className="h-3.5 w-3.5" />
                  {t(tab.labelKey)}
                </span>
              </TabsTrigger>
            );
          })}
        </TabsList>
      </Tabs>

      {activeTab === 'overview' ? (
        <>
          {workbenchError}

          {/* The workbench aggregates follow the customer-list search, which
              now lives on another tab — say so instead of silently narrowing. */}
          {scope.email.length > 0 ? (
            <div
              className="flex flex-wrap items-center gap-3 text-dense text-paper-low"
              data-testid="customer-manager-overview-filtered"
            >
              <Chip tone="signal">
                {t('customer_manager_workspace.overview.filtered', { email: scope.email })}
              </Chip>
              <Button variant="ghost" size="sm" onClick={handleClear}>
                {t('customer_manager_workspace.search.clear')}
              </Button>
            </div>
          ) : null}

          <div className="grid grid-cols-2 gap-3 max-md:grid-flow-dense xl:grid-cols-5">
            <KpiCard
              label={t('customer_manager_workspace.metric.customers')}
              value={fmtInt(portfolio?.assigned_customers ?? 0)}
              testId="customer-manager-metric-customers"
            />
            <KpiCard
              label={t('customer_manager_workspace.metric.balance')}
              value={formatUsdMicrosOrCentsAsCnyDisplayBalance(
                portfolio?.total_balance_usd_micros,
                portfolio?.total_balance_usd_cents ?? 0,
                cnyPerUsd,
              )}
              testId="customer-manager-metric-balance"
              className="max-md:col-span-2"
            />
            <KpiCard
              label={t('customer_manager_workspace.metric.active_keys')}
              value={fmtInt(portfolio?.active_api_keys ?? 0)}
              testId="customer-manager-metric-active-keys"
            />
            <KpiCard
              label={t('customer_manager_workspace.metric.requests')}
              value={fmtInt(portfolio?.requests_30d ?? 0)}
              testId="customer-manager-metric-requests"
            />
            <KpiCard
              label={t('customer_manager_workspace.metric.errors')}
              value={fmtInt(portfolio?.errors_30d ?? 0)}
              testId="customer-manager-metric-errors"
            />
          </div>

          {moneyError}

          {money.isSuccess ? (
            <MoneySection
              money={money.data}
              days={moneyDays}
              onDaysChange={setMoneyDays}
              cnyPerUsd={cnyPerUsd}
            />
          ) : null}

          {query.isSuccess && query.data.risk_queue.length > 0 ? (
            <RiskQueue items={query.data.risk_queue} />
          ) : null}
        </>
      ) : null}

      {activeTab === 'customers' ? (
        <>
          <form
            className="flex flex-wrap items-center gap-3 rounded-lg border border-line-soft bg-card p-4 max-md:p-3"
            onSubmit={handleSearch}
            data-testid="customer-manager-search-form"
            noValidate
          >
            <Input
              type="search"
              value={draftEmail}
              onChange={(e) => setDraftEmail(e.currentTarget.value)}
              placeholder={t('customer_manager_workspace.search.placeholder')}
              aria-label={t('customer_manager_workspace.search.aria_label')}
              data-testid="customer-manager-search-input"
              wrapperClassName="min-w-72 flex-1 max-md:min-w-0 max-md:basis-full"
            />
            <Button
              type="submit"
              variant="primary"
              data-testid="customer-manager-search-submit"
              className="max-md:flex-1"
            >
              {t('customer_manager_workspace.search.submit')}
            </Button>
            {scope.email.length > 0 ? (
              <Button
                variant="ghost"
                onClick={handleClear}
                data-testid="customer-manager-search-clear"
                className="max-md:flex-1"
              >
                {t('customer_manager_workspace.search.clear')}
              </Button>
            ) : null}
          </form>

          {workbenchError}

          {query.isPending ? <LoadingRows /> : null}

          {query.isSuccess && rows.length === 0 ? (
            <EmptyState
              testId="customer-manager-empty"
              title={
                scope.email.length > 0
                  ? t('customer_manager_workspace.empty.filtered.title')
                  : t('customer_manager_workspace.empty.title')
              }
              hint={
                scope.email.length > 0
                  ? t('customer_manager_workspace.empty.filtered.body')
                  : t('customer_manager_workspace.empty.body')
              }
              className="rounded-lg border border-line-soft bg-card"
            />
          ) : null}

          {isMobile && query.isSuccess && rows.length > 0 ? (
            <PortfolioSortControl
              sort={scope.sort}
              allowUnsorted
              columns={sortColumns}
              onSortChange={handleSortChange}
              testIdPrefix="customer-manager"
            />
          ) : null}

          {query.isSuccess && rows.length > 0 ? (
            <>
              {isMobile ? (
                <div data-testid="customer-manager-workspace-cards" className="flex flex-col gap-3">
                  {rows.map((row) => (
                    <PortfolioCard
                      key={row.org_id}
                      customer={row}
                      showOwner={isAdmin}
                      cnyPerUsd={cnyPerUsd}
                      transferPending={pendingByOrgId.has(row.org_id)}
                      canRequestTransfer={
                        user !== null && row.owner.ops_user_id === user.opsUserId
                      }
                      onRequestTransfer={() => setTransferTarget(row)}
                      onRecordReceipt={() => setReceiptTarget(row)}
                    />
                  ))}
                </div>
              ) : (
              <Table testId="customer-manager-workspace-table">
                <THead>
                  <Tr interactive={false}>
                    <SortableTh
                      label={t('customer_manager_workspace.column.customer')}
                      sortKey="email"
                      sort={scope.sort}
                      onSort={handleSort}
                      testId="customer-manager-sort-email"
                    />
                    <SortableTh
                      label={t('customer_manager_workspace.column.org')}
                      sortKey="org"
                      sort={scope.sort}
                      onSort={handleSort}
                      testId="customer-manager-sort-org"
                    />
                    <SortableTh
                      num
                      label={t('customer_manager_workspace.column.balance')}
                      sortKey="balance"
                      sort={scope.sort}
                      onSort={handleSort}
                      testId="customer-manager-sort-balance"
                    />
                    <SortableTh
                      num
                      label={t('customer_manager_workspace.column.spend_30d')}
                      sortKey="spend_30d"
                      sort={scope.sort}
                      onSort={handleSort}
                      testId="customer-manager-sort-spend-30d"
                    />
                    <SortableTh
                      label={t('customer_manager_workspace.column.usage')}
                      sortKey="requests"
                      sort={scope.sort}
                      onSort={handleSort}
                      testId="customer-manager-sort-requests"
                    />
                    <SortableTh
                      label={t('customer_manager_workspace.column.keys')}
                      sortKey="keys"
                      sort={scope.sort}
                      onSort={handleSort}
                      testId="customer-manager-sort-keys"
                    />
                    {isAdmin ? (
                      <SortableTh
                        label={t('customer_manager_workspace.column.owner')}
                        sortKey="owner"
                        sort={scope.sort}
                        onSort={handleSort}
                        testId="customer-manager-sort-owner"
                      />
                    ) : null}
                    <SortableTh
                      label={t('customer_manager_workspace.column.risks')}
                      sortKey="risks"
                      sort={scope.sort}
                      onSort={handleSort}
                      testId="customer-manager-sort-risks"
                    />
                    <Th className="text-right">{t('customer_manager_workspace.column.actions')}</Th>
                  </Tr>
                </THead>
                <TBody>
                  {rows.map((row) => (
                    <PortfolioRow
                      key={row.org_id}
                      customer={row}
                      showOwner={isAdmin}
                      cnyPerUsd={cnyPerUsd}
                      transferPending={pendingByOrgId.has(row.org_id)}
                      canRequestTransfer={
                        user !== null && row.owner.ops_user_id === user.opsUserId
                      }
                      onRequestTransfer={() => setTransferTarget(row)}
                      onRecordReceipt={() => setReceiptTarget(row)}
                    />
                  ))}
                </TBody>
              </Table>
              )}
              <nav
                aria-label={t('customer_manager_workspace.pagination.aria_label')}
                data-testid="customer-manager-pagination"
                className="flex items-center justify-between gap-4 text-dense text-paper-low max-md:flex-wrap max-md:gap-3"
              >
                <span>
                  {t('customer_manager_workspace.pagination.range', {
                    start: currentOffset + 1,
                    end: Math.min(currentOffset + rows.length, total),
                    total,
                  })}
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    disabled={!hasPrev || query.isFetching}
                    onClick={() =>
                      setScope((prev) => ({
                        ...prev,
                        offset: Math.max(0, prev.offset - PAGE_LIMIT),
                      }))
                    }
                    data-testid="customer-manager-pagination-prev"
                  >
                    {t('common.previous')}
                  </Button>
                  <Button
                    size="sm"
                    disabled={!hasNext || query.isFetching}
                    onClick={() => setScope((prev) => ({ ...prev, offset: prev.offset + PAGE_LIMIT }))}
                    data-testid="customer-manager-pagination-next"
                  >
                    {t('common.next')}
                  </Button>
                </div>
              </nav>
            </>
          ) : null}
        </>
      ) : null}

      {activeTab === 'top_spend' ? (
        <>
          {moneyError}

          {money.isSuccess ? (
            <MoneyTopCustomersPanel
              rows={money.data.top_customers}
              cnyPerUsd={cnyPerUsd}
              isMobile={isMobile}
            />
          ) : null}
        </>
      ) : null}

      {activeTab === 'top_up_reminders' ? (
        <>
          {runway.isError ? (
            <ErrorBanner
              testId="customer-manager-runway-error"
              title={t('customer_manager_workspace.runway.error.title')}
              error={runway.error}
              onRetry={() => void runway.refetch()}
            />
          ) : null}

          {runway.isSuccess ? (
            <RunwayPanel
              runway={runway.data}
              threshold={runwayThreshold}
              onThresholdChange={setRunwayThreshold}
              cnyPerUsd={cnyPerUsd}
              isMobile={isMobile}
            />
          ) : null}
        </>
      ) : null}

      {activeTab === 'quotation' ? (
        <QuotationTab
          isAdmin={isAdmin}
          adminBrand={scope.brand}
          opsUserId={user?.opsUserId ?? 'anon'}
        />
      ) : null}

      {transferTarget !== null ? (
        <CustomerTransferRequestDialog
          open
          onOpenChange={(open) => {
            if (!open) setTransferTarget(null);
          }}
          orgId={transferTarget.org_id}
          customerEmail={transferTarget.email}
          customerBrand={transferTarget.signup_brand}
        />
      ) : null}

      {receiptTarget !== null ? (
        <ReceiptSubmitDialog
          open
          onOpenChange={(open) => {
            if (!open) setReceiptTarget(null);
          }}
          orgId={receiptTarget.org_id}
          customerEmail={receiptTarget.email}
          customerBrand={receiptTarget.signup_brand}
        />
      ) : null}
    </section>
  );
}

/**
 * Workbench money views (epic #1035, #1065): owned-book funnel
 * tiles and the 30d/90d consumption trend; the top-customers-by-spend
 * table renders on its own tab. Every figure is the tenant-charge consumption lens (usd_charged)
 * rendered in CNY via the exchange-rate policy pivot — the copy labels
 * it 消费/consumption, never cash revenue.
 */
function MoneySection({
  money,
  days,
  onDaysChange,
  cnyPerUsd,
}: {
  readonly money: CustomerManagerMoneyResponse;
  readonly days: CustomerManagerMoneyTrendDays;
  readonly onDaysChange: (days: CustomerManagerMoneyTrendDays) => void;
  readonly cnyPerUsd: number | null;
}): JSX.Element {
  const t = useT();
  const conversion =
    money.funnel.paying_rate === null
      ? t('customer_manager_workspace.money.funnel.conversion_pending')
      : `${(money.funnel.paying_rate * 100).toFixed(1)}%`;
  return (
    <div className="flex flex-col gap-3" data-testid="customer-manager-money">
      <p className="m-0 text-micro text-paper-low" data-testid="customer-manager-money-note">
        {t('customer_manager_workspace.money.consumption_note')}
      </p>
      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <KpiCard
          label={t('customer_manager_workspace.money.funnel.owned')}
          value={fmtInt(money.funnel.owned_customers)}
          testId="customer-manager-money-funnel-owned"
        />
        <KpiCard
          label={t('customer_manager_workspace.money.funnel.active_30d')}
          value={fmtInt(money.funnel.active_customers)}
          testId="customer-manager-money-funnel-active"
        />
        <KpiCard
          label={t('customer_manager_workspace.money.funnel.paying_30d')}
          value={fmtInt(money.funnel.paying_customers)}
          testId="customer-manager-money-funnel-paying"
        />
        <KpiCard
          label={t('customer_manager_workspace.money.funnel.conversion')}
          value={conversion}
          testId="customer-manager-money-funnel-conversion"
        />
      </div>
      <MoneyTrendPanel money={money} days={days} onDaysChange={onDaysChange} cnyPerUsd={cnyPerUsd} />
    </div>
  );
}

function MoneyTrendPanel({
  money,
  days,
  onDaysChange,
  cnyPerUsd,
}: {
  readonly money: CustomerManagerMoneyResponse;
  readonly days: CustomerManagerMoneyTrendDays;
  readonly onDaysChange: (days: CustomerManagerMoneyTrendDays) => void;
  readonly cnyPerUsd: number | null;
}): JSX.Element {
  const t = useT();
  const rows = money.trend;
  const hasSpend = rows.some((row) => row.usd_charged_micros > 0);
  const maxMicros = Math.max(1, ...rows.map((row) => row.usd_charged_micros));
  const width = 920;
  const height = 200;
  const paddingX = 24;
  const bottomY = 156;
  const chartHeight = 128;
  const groupWidth = Math.max(4, (width - paddingX * 2) / Math.max(1, rows.length));
  const labelEvery = Math.max(1, Math.ceil(rows.length / 6));
  return (
    <Panel
      testId="customer-manager-money-trend"
      caption={t('customer_manager_workspace.money.trend.title')}
      actions={
        <>
          <Button
            size="sm"
            variant={days === 30 ? 'primary' : 'ghost'}
            onClick={() => onDaysChange(30)}
            data-testid="customer-manager-money-days-30"
          >
            {t('customer_manager_workspace.money.trend.days_30')}
          </Button>
          <Button
            size="sm"
            variant={days === 90 ? 'primary' : 'ghost'}
            onClick={() => onDaysChange(90)}
            data-testid="customer-manager-money-days-90"
          >
            {t('customer_manager_workspace.money.trend.days_90')}
          </Button>
        </>
      }
      padded
    >
      {rows.length === 0 || !hasSpend ? (
        <EmptyState
          testId="customer-manager-money-trend-empty"
          title={t('customer_manager_workspace.money.trend.empty')}
        />
      ) : (
        <div className="overflow-x-auto [scrollbar-gutter:stable]">
          <svg
            role="img"
            aria-label={t('customer_manager_workspace.money.trend.aria')}
            data-testid="customer-manager-money-trend-chart"
            viewBox={`0 0 ${width} ${height}`}
            className="block h-52 min-w-[640px] rounded-lg bg-well"
          >
            <line
              x1={paddingX}
              x2={width - paddingX}
              y1={bottomY}
              y2={bottomY}
              strokeWidth={1}
              className="stroke-line"
            />
            {rows.map((row, index) => {
              const x = paddingX + index * groupWidth + groupWidth * 0.2;
              const barWidth = Math.max(2, groupWidth * 0.6);
              const barHeight =
                row.usd_charged_micros === 0
                  ? 0
                  : Math.max(2, Math.round((row.usd_charged_micros / maxMicros) * chartHeight));
              return (
                <g key={row.date} data-testid={`customer-manager-money-trend-day-${row.date}`}>
                  <title>
                    {t('customer_manager_workspace.money.trend.tooltip', {
                      date: row.date,
                      amount: formatUsdMicrosOrCentsAsCnyDisplay(
                        row.usd_charged_micros,
                        row.usd_charged_cents,
                        cnyPerUsd, false),
                      count: row.usage_count,
                    })}
                  </title>
                  {barHeight > 0 ? (
                    <rect
                      x={x}
                      y={bottomY - barHeight}
                      width={barWidth}
                      height={barHeight}
                      rx={1}
                      className="fill-signal"
                    />
                  ) : null}
                  {index % labelEvery === 0 || index === rows.length - 1 ? (
                    <text
                      x={x + barWidth / 2}
                      y={182}
                      textAnchor="middle"
                      className="fill-paper-low font-mono text-micro"
                    >
                      {row.date.slice(5)}
                    </text>
                  ) : null}
                </g>
              );
            })}
          </svg>
        </div>
      )}
    </Panel>
  );
}

function MoneyTopCustomersPanel({
  rows,
  cnyPerUsd,
  isMobile,
}: {
  readonly rows: readonly CustomerManagerMoneyTopCustomer[];
  readonly cnyPerUsd: number | null;
  readonly isMobile: boolean;
}): JSX.Element {
  const t = useT();
  return (
    <Panel
      testId="customer-manager-money-top"
      caption={t('customer_manager_workspace.money.top.title')}
      count={rows.length}
    >
      {rows.length === 0 ? (
        <EmptyState
          testId="customer-manager-money-top-empty"
          title={t('customer_manager_workspace.money.top.empty')}
        />
      ) : isMobile ? (
        <div data-testid="customer-manager-money-top-cards" className="flex flex-col gap-3 p-3">
          {rows.map((row) => (
            <CustomerSummaryCard
              key={row.account_id}
              testId={`customer-manager-money-top-${row.account_id}`}
              company={
                row.org_id !== null ? { orgId: row.org_id } : { accountId: row.account_id }
              }
              title={row.email}
              subtitle={row.org_name ?? truncate(row.account_id)}
              facts={[
                {
                  label: t('customer_manager_workspace.money.top.column.requests'),
                  value: fmtInt(row.usage_count),
                  num: true,
                },
                {
                  label: t('customer_manager_workspace.money.top.column.spend'),
                  value: formatUsdMicrosOrCentsAsCnyDisplay(
                    row.usd_charged_micros,
                    row.usd_charged_cents,
                    cnyPerUsd,
                    false,
                  ),
                  num: true,
                  emphasis: true,
                },
              ]}
              actions={
                row.org_id !== null ? (
                  <div className="grid grid-cols-2 gap-2">
                    <Link
                      to={`/crm/customers/${encodeURIComponent(row.org_id)}?tab=sku_rates`}
                      data-testid={`customer-manager-money-top-${row.account_id}-sku-rates`}
                      className={cx(buttonClass('secondary', 'sm'), 'w-full')}
                    >
                      <Waypoints aria-hidden="true" className="h-3.5 w-3.5" />
                      {t('customer_manager_workspace.action.sku_rates')}
                    </Link>
                    <Link
                      to={`/crm/customers/${encodeURIComponent(row.org_id)}`}
                      data-testid={`customer-manager-money-top-${row.account_id}-open`}
                      className={cx(buttonClass('secondary', 'sm'), 'w-full')}
                    >
                      <ExternalLink aria-hidden="true" className="h-3.5 w-3.5" />
                      {t('customer_manager_workspace.money.top.open_360')}
                    </Link>
                  </div>
                ) : (
                  <span className="text-label text-paper-low">
                    {t('customer_manager_workspace.money.top.unlinked')}
                  </span>
                )
              }
            />
          ))}
        </div>
      ) : (
        <Table testId="customer-manager-money-top-table" className="rounded-none border-0">
          <THead>
            <Tr interactive={false}>
              <Th>{t('customer_manager_workspace.money.top.column.customer')}</Th>
              <Th num>{t('customer_manager_workspace.money.top.column.requests')}</Th>
              <Th num>{t('customer_manager_workspace.money.top.column.spend')}</Th>
              <Th className="text-right">
                {t('customer_manager_workspace.money.top.column.actions')}
              </Th>
            </Tr>
          </THead>
          <TBody>
            {rows.map((row) => (
              <Tr key={row.account_id} data-testid={`customer-manager-money-top-${row.account_id}`}>
                <Td>
                  <CustomerCell
                    orgId={row.org_id ?? undefined}
                    accountId={row.org_id === null ? row.account_id : undefined}
                    secondary={
                      <div className="flex min-w-0 flex-col gap-1">
                        <span className="max-w-64 truncate text-body font-medium text-paper">
                          {row.email}
                        </span>
                        <span className="font-mono text-micro text-paper-low">
                          {row.org_name ?? truncate(row.account_id)}
                        </span>
                      </div>
                    }
                  />
                </Td>
                <Td num>{fmtInt(row.usage_count)}</Td>
                <Td num emphasis>
                  {formatUsdMicrosOrCentsAsCnyDisplay(
                    row.usd_charged_micros,
                    row.usd_charged_cents,
                    cnyPerUsd, false)}
                </Td>
                <Td className="text-right">
                  {row.org_id !== null ? (
                    <div className="flex items-center justify-end gap-2">
                      <Link
                        to={`/crm/customers/${encodeURIComponent(row.org_id)}?tab=sku_rates`}
                        data-testid={`customer-manager-money-top-${row.account_id}-sku-rates`}
                        className={buttonClass('secondary', 'sm')}
                      >
                        <Waypoints aria-hidden="true" className="h-3.5 w-3.5" />
                        {t('customer_manager_workspace.action.sku_rates')}
                      </Link>
                      <Link
                        to={`/crm/customers/${encodeURIComponent(row.org_id)}`}
                        data-testid={`customer-manager-money-top-${row.account_id}-open`}
                        className={buttonClass('secondary', 'sm')}
                      >
                        <ExternalLink aria-hidden="true" className="h-3.5 w-3.5" />
                        {t('customer_manager_workspace.money.top.open_360')}
                      </Link>
                    </div>
                  ) : (
                    // No org on the row means the spend could not be joined
                    // to a customer record in this view's book — an account
                    // without an org, or (for an admin's cross-book view) one
                    // outside the listed scope. It is NOT an ownership state:
                    // released customers drop out of the owner-scoped revenue
                    // aggregate entirely, so they never reach this table.
                    <span className="text-label text-paper-low">
                      {t('customer_manager_workspace.money.top.unlinked')}
                    </span>
                  )}
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}
    </Panel>
  );
}

/** Chip severity for a days-left figure: ≤3d critical, ≤7d warning. */
function runwayVariant(daysLeft: number): 'error' | 'warning' | 'neutral' {
  if (daysLeft <= 3) return 'error';
  if (daysLeft <= 7) return 'warning';
  return 'neutral';
}

/**
 * 催充值 runway panel (epic #1035 E6-D2, #1066): owned customers whose
 * burn-based days-left falls under the selected threshold, soonest to
 * run dry first, each row deep-linking to the Customer 360. Replaces
 * the point-in-time low-balance signal as the collections driver.
 * Money figures are the tenant-charge consumption lens rendered in CNY
 * via the exchange-rate policy pivot.
 */
function RunwayPanel({
  runway,
  threshold,
  onThresholdChange,
  cnyPerUsd,
  isMobile,
}: {
  readonly runway: CustomerManagerRunwayResponse;
  readonly threshold: CustomerManagerRunwayThresholdDays;
  readonly onThresholdChange: (threshold: CustomerManagerRunwayThresholdDays) => void;
  readonly cnyPerUsd: number | null;
  readonly isMobile: boolean;
}): JSX.Element {
  const t = useT();
  // Server order is already days_left ASC; re-sort defensively so the
  // chase order survives any transport reordering.
  const rows = runway.customers
    .filter((row) => row.days_left <= threshold)
    .sort((a, b) => a.days_left - b.days_left || a.account_id.localeCompare(b.account_id));
  return (
    <Panel
      testId="customer-manager-runway"
      caption={t('customer_manager_workspace.runway.title')}
      count={rows.length}
      actions={
        <div
          role="group"
          aria-label={t('customer_manager_workspace.runway.threshold_aria')}
          className="flex items-center gap-1"
        >
          {CUSTOMER_MANAGER_RUNWAY_THRESHOLD_OPTIONS.map((option) => (
            <Button
              key={option}
              size="sm"
              variant={threshold === option ? 'primary' : 'ghost'}
              onClick={() => onThresholdChange(option)}
              data-testid={`customer-manager-runway-threshold-${option}`}
            >
              {t('customer_manager_workspace.runway.threshold', { days: option })}
            </Button>
          ))}
        </div>
      }
    >
      <p className="m-0 px-4 pt-3 text-micro text-paper-low" data-testid="customer-manager-runway-note">
        {t('customer_manager_workspace.runway.note', { days: runway.burn_window.days })}
      </p>
      {rows.length === 0 ? (
        <EmptyState
          testId="customer-manager-runway-empty"
          title={t('customer_manager_workspace.runway.empty', { days: threshold })}
        />
      ) : isMobile ? (
        <div data-testid="customer-manager-runway-cards" className="flex flex-col gap-3 p-3">
          {rows.map((row) => (
            <CustomerSummaryCard
              key={row.account_id}
              testId={`customer-manager-runway-${row.account_id}`}
              company={
                row.org_id !== null ? { orgId: row.org_id } : { accountId: row.account_id }
              }
              title={row.email}
              subtitle={row.org_name ?? truncate(row.account_id)}
              badges={
                <StatusChip
                  variant={runwayVariant(row.days_left)}
                  label={t('customer_manager_workspace.runway.days_value', {
                    days: row.days_left,
                  })}
                  testId={`customer-manager-runway-${row.account_id}-days`}
                />
              }
              facts={[
                {
                  label: t('customer_manager_workspace.runway.column.remaining'),
                  value: formatUsdMicrosOrCentsAsCnyDisplay(
                    row.remaining_usd_micros,
                    row.remaining_usd_cents,
                    cnyPerUsd,
                    false,
                  ),
                  num: true,
                  emphasis: true,
                },
                {
                  label: t('customer_manager_workspace.runway.column.burn_per_day'),
                  value: formatUsdMicrosOrCentsAsCnyDisplay(
                    row.burn_per_day_usd_micros,
                    row.burn_per_day_usd_cents,
                    cnyPerUsd,
                    false,
                  ),
                  num: true,
                },
              ]}
              actions={
                row.org_id !== null ? (
                  <div className="grid grid-cols-2 gap-2">
                    <Link
                      to={`/crm/customers/${encodeURIComponent(row.org_id)}?tab=sku_rates`}
                      data-testid={`customer-manager-runway-${row.account_id}-sku-rates`}
                      className={cx(buttonClass('secondary', 'sm'), 'w-full')}
                    >
                      <Waypoints aria-hidden="true" className="h-3.5 w-3.5" />
                      {t('customer_manager_workspace.action.sku_rates')}
                    </Link>
                    <Link
                      to={`/crm/customers/${encodeURIComponent(row.org_id)}`}
                      data-testid={`customer-manager-runway-${row.account_id}-open`}
                      className={cx(buttonClass('secondary', 'sm'), 'w-full')}
                    >
                      <ExternalLink aria-hidden="true" className="h-3.5 w-3.5" />
                      {t('customer_manager_workspace.runway.open_360')}
                    </Link>
                  </div>
                ) : (
                  <span className="text-label text-paper-low">—</span>
                )
              }
            />
          ))}
        </div>
      ) : (
        <Table testId="customer-manager-runway-table" className="rounded-none border-0">
          <THead>
            <Tr interactive={false}>
              <Th>{t('customer_manager_workspace.runway.column.customer')}</Th>
              <Th>{t('customer_manager_workspace.runway.column.days_left')}</Th>
              <Th num>{t('customer_manager_workspace.runway.column.remaining')}</Th>
              <Th num>{t('customer_manager_workspace.runway.column.burn_per_day')}</Th>
              <Th className="text-right">
                {t('customer_manager_workspace.runway.column.actions')}
              </Th>
            </Tr>
          </THead>
          <TBody>
            {rows.map((row) => (
              <Tr key={row.account_id} data-testid={`customer-manager-runway-${row.account_id}`}>
                <Td>
                  <CustomerCell
                    orgId={row.org_id ?? undefined}
                    accountId={row.org_id === null ? row.account_id : undefined}
                    secondary={
                      <div className="flex min-w-0 flex-col gap-1">
                        <span className="max-w-64 truncate text-body font-medium text-paper">
                          {row.email}
                        </span>
                        <span className="font-mono text-micro text-paper-low">
                          {row.org_name ?? truncate(row.account_id)}
                        </span>
                      </div>
                    }
                  />
                </Td>
                <Td>
                  <StatusChip
                    variant={runwayVariant(row.days_left)}
                    label={t('customer_manager_workspace.runway.days_value', {
                      days: row.days_left,
                    })}
                    testId={`customer-manager-runway-${row.account_id}-days`}
                  />
                </Td>
                <Td num emphasis>
                  {formatUsdMicrosOrCentsAsCnyDisplay(
                    row.remaining_usd_micros,
                    row.remaining_usd_cents,
                    cnyPerUsd, false)}
                </Td>
                <Td num>
                  {formatUsdMicrosOrCentsAsCnyDisplay(
                    row.burn_per_day_usd_micros,
                    row.burn_per_day_usd_cents,
                    cnyPerUsd, false)}
                </Td>
                <Td className="text-right">
                  {row.org_id !== null ? (
                    <div className="flex items-center justify-end gap-2">
                      <Link
                        to={`/crm/customers/${encodeURIComponent(row.org_id)}?tab=sku_rates`}
                        data-testid={`customer-manager-runway-${row.account_id}-sku-rates`}
                        className={buttonClass('secondary', 'sm')}
                      >
                        <Waypoints aria-hidden="true" className="h-3.5 w-3.5" />
                        {t('customer_manager_workspace.action.sku_rates')}
                      </Link>
                      <Link
                        to={`/crm/customers/${encodeURIComponent(row.org_id)}`}
                        data-testid={`customer-manager-runway-${row.account_id}-open`}
                        className={buttonClass('secondary', 'sm')}
                      >
                        <ExternalLink aria-hidden="true" className="h-3.5 w-3.5" />
                        {t('customer_manager_workspace.runway.open_360')}
                      </Link>
                    </div>
                  ) : (
                    <span className="text-label text-paper-low">—</span>
                  )}
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}
    </Panel>
  );
}

/**
 * How many risk rows each expand step reveals. 20 keeps the tab scannable on
 * a cross-brand book with >1k items; the panel header count still carries the
 * full total.
 */
const RISK_QUEUE_PAGE = 20;

/** Whether another page step is available with `remaining` rows still hidden. */
function hasMore(remaining: number): boolean {
  return remaining > RISK_QUEUE_PAGE;
}

function RiskQueue({
  items,
}: {
  readonly items: readonly CustomerManagerRiskQueueItem[];
}): JSX.Element {
  const t = useT();
  const [shown, setShown] = useState(RISK_QUEUE_PAGE);
  const listId = useId();
  const moreRef = useRef<HTMLButtonElement>(null);
  const allRef = useRef<HTMLButtonElement>(null);
  const collapseRef = useRef<HTMLButtonElement>(null);
  const pendingFocusRef = useRef<'all' | 'collapse' | 'expand' | null>(null);
  const visible = items.slice(0, shown);
  const remaining = Math.max(0, items.length - visible.length);

  // A refetch can shrink the queue below one page. Start the next growth from
  // a fresh 20-row preview instead of keeping a stale expanded depth.
  useEffect(() => {
    if (items.length <= RISK_QUEUE_PAGE) {
      setShown(RISK_QUEUE_PAGE);
    }
  }, [items.length]);

  // A page step can unmount the button that held focus (the last "show more",
  // or "show all" / "collapse" reversing direction). Handlers record where
  // focus should land; this runs after the new rows commit and clears the
  // request so a no-op render never steals focus.
  useEffect(() => {
    const target = pendingFocusRef.current;
    if (target === null) {
      return;
    }
    pendingFocusRef.current = null;
    if (target === 'all') {
      allRef.current?.focus();
    } else if (target === 'collapse') {
      collapseRef.current?.focus();
    } else {
      (moreRef.current ?? allRef.current)?.focus();
    }
  }, [shown]);

  return (
    <Panel
      testId="customer-manager-risk-queue"
      caption={t('customer_manager_workspace.risk_queue.title')}
      count={items.length}
      padded
    >
      <ul id={listId} className="flex flex-col gap-2">
        {visible.map((item) => (
          <li key={item.org_id} className="min-w-0">
            <Link
              to={`/crm/customers/${encodeURIComponent(item.org_id)}`}
              data-testid={`customer-manager-risk-${item.org_id}`}
              className="flex items-center gap-4 rounded-lg border border-line bg-well px-3 py-2.5 text-left transition-colors duration-120 hover:border-line-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40 max-md:flex-wrap"
            >
              <span className="flex w-32 shrink-0 max-md:order-1 max-md:w-auto">
                <StatusChip
                  variant={riskVariant(item.primary_signal)}
                  label={item.primary_signal.label}
                />
              </span>
              <span className="flex w-64 min-w-0 shrink-0 flex-col gap-0.5 max-md:order-3 max-md:w-full">
                <CustomerCompanyLine orgId={item.org_id} className="max-w-full" />
                <span className="max-w-full truncate text-body font-medium text-paper">
                  {item.email}
                </span>
              </span>
              {/* Server-built signal facts (severity, amount, reason for
                  merged finance items, #1070) — read-only: resolve/replay
                  stays on the admin surfaces. */}
              <span
                className="min-w-0 flex-1 truncate text-micro text-paper-low max-md:order-4 max-md:basis-full max-md:whitespace-normal max-md:line-clamp-2"
                title={item.primary_signal.detail}
                data-testid={`customer-manager-risk-${item.org_id}-detail`}
              >
                {item.primary_signal.detail}
              </span>
              <span className="shrink-0 whitespace-nowrap text-right text-micro text-paper-low max-md:order-2 max-md:ml-auto">
                {t('customer_manager_workspace.risk_queue.item_detail', {
                  count: item.risk_count,
                })}
              </span>
            </Link>
          </li>
        ))}
      </ul>
      {items.length > RISK_QUEUE_PAGE ? (
        <div className="mt-2 flex flex-wrap justify-center gap-2">
          {hasMore(remaining) ? (
            <Button
              ref={moreRef}
              size="sm"
              variant="ghost"
              aria-controls={listId}
              data-testid="customer-manager-risk-queue-more"
              onClick={() => {
                // The clicked button disappears once this step exhausts the
                // hidden rows; hand focus to "show all", which always survives
                // (a "more" step never reveals the very last row).
                if (!hasMore(remaining - RISK_QUEUE_PAGE)) {
                  pendingFocusRef.current = 'all';
                }
                setShown((n) => n + RISK_QUEUE_PAGE);
              }}
            >
              {t('customer_manager_workspace.risk_queue.show_more', {
                count: RISK_QUEUE_PAGE,
              })}
            </Button>
          ) : null}
          {remaining > 0 ? (
            <Button
              ref={allRef}
              size="sm"
              variant="ghost"
              aria-controls={listId}
              data-testid="customer-manager-risk-queue-all"
              onClick={() => {
                pendingFocusRef.current = 'collapse';
                setShown(items.length);
              }}
            >
              {t('customer_manager_workspace.risk_queue.show_all', {
                count: remaining,
              })}
            </Button>
          ) : null}
          {visible.length > RISK_QUEUE_PAGE ? (
            <Button
              ref={collapseRef}
              size="sm"
              variant="ghost"
              aria-controls={listId}
              data-testid="customer-manager-risk-queue-collapse"
              onClick={() => {
                pendingFocusRef.current = 'expand';
                setShown(RISK_QUEUE_PAGE);
              }}
            >
              {t('customer_manager_workspace.risk_queue.collapse')}
            </Button>
          ) : null}
        </div>
      ) : null}
    </Panel>
  );
}

function PortfolioRow({
  customer,
  showOwner,
  cnyPerUsd,
  transferPending,
  canRequestTransfer,
  onRequestTransfer,
  onRecordReceipt,
}: {
  readonly customer: CustomerManagerPortfolioCustomer;
  readonly showOwner: boolean;
  readonly cnyPerUsd: number | null;
  readonly transferPending: boolean;
  readonly canRequestTransfer: boolean;
  readonly onRequestTransfer: () => void;
  readonly onRecordReceipt: () => void;
}): JSX.Element {
  const t = useT();
  return (
    <Tr data-testid={`customer-manager-row-${customer.org_id}`}>
      <Td>
        <CustomerCell
          orgId={customer.org_id}
          secondary={
            <div className="flex min-w-0 flex-col gap-1">
              <span className="max-w-64 truncate text-body font-medium text-paper">
                {customer.email}
              </span>
              <span className="font-mono text-micro text-paper-low">{truncate(customer.account_id)}</span>
              {transferPending ? (
                <StatusChip
                  variant="warning"
                  label={t('customer_transfer.pending_chip')}
                  testId={`customer-manager-row-${customer.org_id}-transfer-pending`}
                />
              ) : null}
            </div>
          }
        />
      </Td>
      <Td>
        <div className="flex min-w-0 flex-col gap-1">
          <span className="max-w-56 truncate">{customer.org_name}</span>
          <span className="font-mono text-micro text-paper-low">{truncate(customer.org_id)}</span>
        </div>
      </Td>
      <Td num emphasis>
        {formatUsdMicrosOrCentsAsCnyDisplayBalance(
          customer.balance_usd_micros,
          customer.balance_usd_cents,
          cnyPerUsd,
        )}
      </Td>
      {/* Per-customer 30d consumption (#1065 AC 3): sourced from the
          existing usage-posture cost field — no contract change. */}
      <Td num data-testid={`customer-manager-row-${customer.org_id}-spend-30d`}>
        {formatUsdMicrosOrCentsAsCnyDisplay(
          customer.usage.cost_usd_micros,
          customer.usage.cost_usd_cents,
          cnyPerUsd, false)}
      </Td>
      <Td>
        <div className="flex flex-col gap-1">
          <span className="font-mono text-paper">{fmtInt(customer.usage.request_count)} req</span>
          <span className="text-label text-paper-low">
            {customer.usage.error_count > 0
              ? t('customer_manager_workspace.usage.errors', {
                  count: customer.usage.error_count,
                })
              : t('customer_manager_workspace.usage.no_errors')}
          </span>
        </div>
      </Td>
      <Td>
        <div className="flex flex-col gap-1">
          <span className="text-paper">
            {t('customer_manager_workspace.keys.active', {
              active: customer.api_keys.active,
              total: customer.api_keys.total,
            })}
          </span>
          <span className="text-label text-paper-low">
            {t('customer_manager_workspace.keys.last_used', {
              time: formatLocalTimestamp(customer.api_keys.last_used_at),
            })}
          </span>
        </div>
      </Td>
      {showOwner ? (
        <Td>
          <div className="flex flex-col gap-1">
            <span className="max-w-44 truncate">{ownerLabel(customer, t)}</span>
            <span className="font-mono text-micro text-paper-low">
              {formatLocalTimestamp(customer.owner.assigned_at)}
            </span>
          </div>
        </Td>
      ) : null}
      <Td>
        <div className="flex max-w-72 flex-wrap gap-1">
          {customer.risk_signals.length === 0 ? (
            <StatusChip variant="ok" label={t('customer_manager_workspace.risk.none')} />
          ) : (
            customer.risk_signals.slice(0, 3).map((signal) => (
              <StatusChip key={signal.code} variant={riskVariant(signal)} label={signal.label} />
            ))
          )}
        </div>
      </Td>
      <Td className="text-right">
        <div className="flex items-center justify-end gap-2">
          <Link
            to={`/crm/customers/${encodeURIComponent(customer.org_id)}?tab=sku_rates`}
            data-testid={`customer-manager-row-${customer.org_id}-sku-rates`}
            className={buttonClass('secondary', 'sm')}
          >
            <Waypoints aria-hidden="true" className="h-3.5 w-3.5" />
            {t('customer_manager_workspace.action.sku_rates')}
          </Link>
          <Button
            size="sm"
            variant="ghost"
            onClick={onRecordReceipt}
            data-testid={`customer-manager-row-${customer.org_id}-receipt`}
          >
            <HandCoins aria-hidden="true" className="h-3.5 w-3.5" />
            {t('customer_manager_workspace.action.record_receipt')}
          </Button>
          {canRequestTransfer ? (
            <Button
              size="sm"
              variant="ghost"
              disabled={transferPending}
              onClick={onRequestTransfer}
              data-testid={`customer-manager-row-${customer.org_id}-transfer`}
            >
              <ArrowLeftRight aria-hidden="true" className="h-3.5 w-3.5" />
              {t('customer_transfer.action')}
            </Button>
          ) : null}
          <Link
            to={`/crm/customers/${encodeURIComponent(customer.org_id)}`}
            data-testid={`customer-manager-row-${customer.org_id}-open`}
            className={buttonClass('secondary', 'sm')}
          >
            <ExternalLink aria-hidden="true" className="h-3.5 w-3.5" />
            {t('customer_manager_workspace.action.customer_detail')}
          </Link>
        </div>
      </Td>
    </Tr>
  );
}

function PortfolioCard({
  customer,
  showOwner,
  cnyPerUsd,
  transferPending,
  canRequestTransfer,
  onRequestTransfer,
  onRecordReceipt,
}: {
  readonly customer: CustomerManagerPortfolioCustomer;
  readonly showOwner: boolean;
  readonly cnyPerUsd: number | null;
  readonly transferPending: boolean;
  readonly canRequestTransfer: boolean;
  readonly onRequestTransfer: () => void;
  readonly onRecordReceipt: () => void;
}): JSX.Element {
  const t = useT();
  const facts: CustomerSummaryFact[] = [
    {
      label: t('customer_manager_workspace.column.balance'),
      value: formatUsdMicrosOrCentsAsCnyDisplayBalance(
        customer.balance_usd_micros,
        customer.balance_usd_cents,
        cnyPerUsd,
      ),
      num: true,
      emphasis: true,
    },
    {
      label: t('customer_manager_workspace.column.spend_30d'),
      value: formatUsdMicrosOrCentsAsCnyDisplay(
        customer.usage.cost_usd_micros,
        customer.usage.cost_usd_cents,
        cnyPerUsd,
        false,
      ),
      num: true,
      testId: `customer-manager-row-${customer.org_id}-spend-30d`,
    },
    {
      label: t('customer_manager_workspace.column.usage'),
      value: (
        <span className="flex flex-col">
          <span>
            {fmtInt(customer.usage.request_count)} req
          </span>
          <span>
            {customer.usage.error_count > 0
              ? t('customer_manager_workspace.usage.errors', {
                  count: customer.usage.error_count,
                })
              : t('customer_manager_workspace.usage.no_errors')}
          </span>
        </span>
      ),
    },
    {
      label: t('customer_manager_workspace.column.keys'),
      value: (
        <span className="flex flex-col">
          <span>
            {t('customer_manager_workspace.keys.active', {
              active: customer.api_keys.active,
              total: customer.api_keys.total,
            })}
          </span>
          <span>
            {t('customer_manager_workspace.keys.last_used', {
              time: formatLocalTimestamp(customer.api_keys.last_used_at),
            })}
          </span>
        </span>
      ),
    },
  ];
  if (showOwner) {
    facts.push({
      label: t('customer_manager_workspace.column.owner'),
      value: (
        <span className="flex flex-col">
          <span>{ownerLabel(customer, t)}</span>
          <span>{formatLocalTimestamp(customer.owner.assigned_at)}</span>
        </span>
      ),
    });
  }
  return (
    <CustomerSummaryCard
      testId={`customer-manager-row-${customer.org_id}`}
      company={{ orgId: customer.org_id }}
      title={customer.email}
      subtitle={customer.org_name}
      idLine={`${truncate(customer.account_id)} · ${truncate(customer.org_id)}`}
      badges={
        transferPending ? (
          <StatusChip
            variant="warning"
            label={t('customer_transfer.pending_chip')}
            testId={`customer-manager-row-${customer.org_id}-transfer-pending`}
          />
        ) : undefined
      }
      facts={facts}
      extra={
        customer.risk_signals.length === 0 ? (
          <StatusChip variant="ok" label={t('customer_manager_workspace.risk.none')} />
        ) : (
          customer.risk_signals.slice(0, 3).map((signal) => (
            <StatusChip key={signal.code} variant={riskVariant(signal)} label={signal.label} />
          ))
        )
      }
      actions={
        <>
          <Link
            to={`/crm/customers/${encodeURIComponent(customer.org_id)}`}
            data-testid={`customer-manager-row-${customer.org_id}-open`}
            className={cx(buttonClass('secondary', 'md'), 'w-full')}
          >
            <ExternalLink aria-hidden="true" className="h-3.5 w-3.5" />
            {t('customer_manager_workspace.action.customer_detail')}
          </Link>
          <div className="grid grid-cols-2 gap-2">
            <Link
              to={`/crm/customers/${encodeURIComponent(customer.org_id)}?tab=sku_rates`}
              data-testid={`customer-manager-row-${customer.org_id}-sku-rates`}
              className={cx(buttonClass('secondary', 'sm'), 'w-full')}
            >
              <Waypoints aria-hidden="true" className="h-3.5 w-3.5" />
              {t('customer_manager_workspace.action.sku_rates')}
            </Link>
            <Button
              size="sm"
              variant="ghost"
              onClick={onRecordReceipt}
              data-testid={`customer-manager-row-${customer.org_id}-receipt`}
              className="w-full"
            >
              <HandCoins aria-hidden="true" className="h-3.5 w-3.5" />
              {t('customer_manager_workspace.action.record_receipt')}
            </Button>
          </div>
          {canRequestTransfer ? (
            <Button
              size="sm"
              variant="ghost"
              disabled={transferPending}
              onClick={onRequestTransfer}
              data-testid={`customer-manager-row-${customer.org_id}-transfer`}
              className="w-full"
            >
              <ArrowLeftRight aria-hidden="true" className="h-3.5 w-3.5" />
              {t('customer_transfer.action')}
            </Button>
          ) : null}
        </>
      }
    />
  );
}

function LoadingRows(): JSX.Element {
  return (
    <div
      data-testid="customer-manager-loading"
      className="flex flex-col gap-2 rounded-lg border border-line-soft bg-card p-4"
    >
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="h-12 animate-pulse rounded-lg bg-raised"
          aria-hidden="true"
        />
      ))}
    </div>
  );
}

function ErrorBanner({
  testId,
  title,
  error,
  onRetry,
}: {
  readonly testId: string;
  readonly title: string;
  readonly error: Error;
  readonly onRetry: () => void;
}): JSX.Element {
  const t = useT();
  return (
    <div
      role="alert"
      data-testid={testId}
      className="flex items-center justify-between gap-4 rounded-lg border border-bad-line bg-bad-dim/50 px-4 py-3 max-md:flex-wrap max-md:gap-3"
    >
      <div className="flex flex-col gap-1">
        <span className="text-body font-medium text-bad-bright">{title}</span>
        <span className="text-dense text-bad">
          {error instanceof OpsApiError
            ? `${error.code}: ${error.message}`
            : t('common.network_error')}
        </span>
      </div>
      <Button variant="danger" size="sm" onClick={onRetry}>
        {t('common.retry')}
      </Button>
    </div>
  );
}
